// Content for the SEO landing pages. Each entry renders via pages/LandingPage.jsx.
import {
  Sparkles, FileSignature, FileText, Languages, ScanSearch,
  GraduationCap, Scale, BookOpen, ShieldCheck, Crown, Award,
} from "lucide-react";

export const LANDING_PAGES = {
  "ai-legal-research": {
    icon: Sparkles,
    eyebrow: "AI Legal Research",
    h1: "AI Legal Research for Indian Law — JusticeVerse",
    title: "AI Legal Research Tool for Indian Law | JusticeVerse",
    description:
      "Run AI legal research grounded in Indian law — BNS, BNSS, IPC, CrPC, CPC, the Constitution and landmark Supreme Court & High Court judgments. Accurate, multilingual answers in seconds with JusticeVerse.",
    intro:
      "JusticeVerse AI is a purpose-built legal research assistant for Indian law. Ask any question on statutes, sections or case law and get precise, structured answers — with relevant provisions, ratio decidendi and citations — without wading through thousands of pages. It works across 11+ Indian languages and reads images and PDFs of legal documents too.",
    features: [
      "Grounded in Indian statutes — BNS, BNSS, Bharatiya Sakshya Adhiniyam, IPC, CrPC, CPC, Evidence Act and the Constitution.",
      "Strict anti-fabrication policy — cites only verified, real judgments and abstains rather than inventing fake citations.",
      "Structured judgment analysis: Facts, Issues, Arguments, Ratio, Holding and Conclusion.",
      "Multilingual answers in Hindi, Gujarati, Marathi, Tamil, Telugu, Bengali and more.",
      "Conversational memory — ask follow-up questions and it remembers the full context.",
    ],
    faqs: [
      { q: "Is JusticeVerse AI legal research accurate?", a: "JusticeVerse follows a strict accuracy policy: it only cites judgments it is confident are real and clearly says when no verified judgment exists, instead of fabricating citations. Always confirm citations against the official record before relying on them in court." },
      { q: "Which laws does it cover?", a: "Indian law including the new criminal codes (BNS, BNSS, Bharatiya Sakshya Adhiniyam), IPC, CrPC, CPC, the Evidence Act, the Constitution, and judgments of the Supreme Court, High Courts and tribunals." },
      { q: "Can it research in Hindi or regional languages?", a: "Yes. You can ask questions and receive answers in 11+ Indian languages while keeping statute and case names in standard form." },
    ],
    cta: { to: "/justicebot", label: "Start Legal Research Free" },
  },

  "ai-legal-drafting": {
    icon: FileSignature,
    eyebrow: "AI Legal Drafting",
    h1: "AI Legal Drafting — Petitions, Applications & Agreements",
    title: "AI Legal Drafting Software for India | JusticeVerse Counsel AI",
    description:
      "Draft bail applications, petitions, notices, pleadings and agreements in minutes with JusticeVerse Counsel AI. Court-ready Indian legal documents with correct structure, clauses and statutory references.",
    intro:
      "Counsel AI is JusticeVerse's legal drafting engine. Describe your matter and instantly get a complete, professionally formatted Indian legal document — with the correct cause title, grounds, prayer/relief clauses, verification and statutory references — ready to edit and file. It cites only real precedents and never invents authorities.",
    features: [
      "Generate bail/anticipatory bail applications, writ petitions, plaints, legal notices, agreements and more.",
      "Correct Indian court formatting — cause titles, numbered grounds, prayer clauses and verification.",
      "Clean plain-text output you can paste straight into your filing software.",
      "Cites only genuine precedents; uses the correct statute when a case can't be verified.",
      "Drafts in English and major Indian languages.",
    ],
    faqs: [
      { q: "What documents can Counsel AI draft?", a: "Applications, petitions, pleadings, legal notices, affidavits and commercial agreements, following standard Indian court and commercial conventions." },
      { q: "Are the drafts court-ready?", a: "They are complete, professionally structured first drafts with placeholders for specifics. Always review and tailor them before filing." },
      { q: "Does it cite fake cases?", a: "No. Counsel AI only cites precedents it is confident are real and relies on the relevant statute when a case cannot be verified." },
    ],
    cta: { to: "/counsel", label: "Draft a Document Now" },
  },

  "ai-case-brief": {
    icon: FileText,
    eyebrow: "AI Case Brief",
    h1: "AI Case Brief Generator for Judgments & Case Files",
    title: "AI Case Brief Generator | Summarise Judgments | JusticeVerse",
    description:
      "Upload any judgment or case file — even scanned/image PDFs — and get an instant case brief: facts, issues, ratio decidendi, holding and citations. Then chat with the document. JusticeVerse CaseBrief AI.",
    intro:
      "CaseBrief AI turns long judgments and case files into clear, structured briefs in seconds — and lets you have a conversation with the document afterwards. Upload a PDF, DOCX or even a scanned/photographed image-only PDF; CaseBrief reads it with OCR and extracts the facts, issues, arguments, ratio and holding.",
    features: [
      "Reads scanned and image-only PDFs automatically using OCR — up to 2000 pages.",
      "Extracts Facts, Issues, Arguments, Ratio Decidendi, Holding and key citations.",
      "Conversational memory — ask follow-up questions about the same document.",
      "Build arguments for either side from the same judgment.",
      "Everyone gets free CaseBriefs to start, including students.",
    ],
    faqs: [
      { q: "Can it read scanned or handwritten-era PDFs?", a: "Yes. CaseBrief detects scanned/image-only PDFs and reads them with OCR, then briefs and lets you chat with the content." },
      { q: "How large a document can I upload?", a: "Up to 100 MB and around 2000 pages per document, including large scanned case files." },
      { q: "Do students get access?", a: "Yes — every user, including students, gets free CaseBriefs to start." },
    ],
    cta: { to: "/casebrief", label: "Summarise a Judgment" },
  },

  "document-translation": {
    icon: Languages,
    eyebrow: "Legal Document Translation",
    h1: "Legal Document Translation AI — Verse Translation",
    title: "AI Legal Document Translation (Hindi & 23 Languages) | JusticeVerse",
    description:
      "Translate any legal document — judgments, contracts, FIRs, notices — into English or 23 languages while preserving the original format. Upload an image or PDF in Hindi or any regional language. JusticeVerse Verse Translation.",
    intro:
      "Verse Translation translates any legal document into English or any of 23 languages while preserving the original layout — headings, numbering, tables and signature blocks. Upload a PDF, DOCX, image or scanned page (even a photo of a Hindi or regional-language document) and get a clean, format-faithful translation, with names, dates, amounts and section numbers kept intact.",
    features: [
      "Translate between 23 languages including Hindi, Gujarati, Marathi, Tamil, Telugu, Bengali, Urdu and English.",
      "Reads scanned / photographed documents with OCR, then translates.",
      "Preserves the original format — headings, lists, tables and signature blocks.",
      "Keeps proper nouns, case numbers, dates, amounts and section numbers unchanged.",
      "Side-by-side original vs translation, with copy and download.",
    ],
    faqs: [
      { q: "Can I translate a photo or scanned PDF of a Hindi document?", a: "Yes. Upload an image or scanned PDF in Hindi or any language; Verse Translation reads it with OCR and translates it into your chosen language." },
      { q: "Which languages are supported?", a: "23 languages including all major Indian languages and English, French, Spanish, German, Arabic and more." },
      { q: "Is the formatting preserved?", a: "Yes — the translation mirrors the original document's headings, numbering and layout." },
    ],
    cta: { to: "/verse-translation", label: "Translate a Document" },
  },

  "document-analysis": {
    icon: ScanSearch,
    eyebrow: "Legal Document Analysis",
    h1: "AI Legal Document Analysis — Read, Brief & Translate",
    title: "AI Legal Document Analysis for India | JusticeVerse",
    description:
      "Analyse any legal document with AI — extract issues, summarise judgments, review contracts, OCR scanned files and translate them. JusticeVerse reads PDFs, DOCX and images in any Indian language.",
    intro:
      "JusticeVerse analyses legal documents end to end. Upload a judgment, contract, FIR or notice — including scanned and image-only files — and the platform reads it with OCR, extracts the key issues and ratio, builds arguments, and can translate it into any language. It's a complete document-intelligence workspace for Indian legal professionals.",
    features: [
      "OCR for scanned and image-only PDFs (up to 2000 pages).",
      "Extract issues, obligations, dates, parties and key clauses.",
      "Summarise judgments into structured case briefs.",
      "Translate analysed documents into 23 languages.",
      "Ask follow-up questions with full document memory.",
    ],
    faqs: [
      { q: "What file types can I analyse?", a: "PDF, DOCX, TXT and images (JPG/PNG and more), including scanned/image-only PDFs read via OCR." },
      { q: "Can it analyse contracts as well as judgments?", a: "Yes — judgments, contracts, FIRs, notices, agreements and other legal documents." },
      { q: "Is my document data private?", a: "Documents are processed to generate your analysis and stored against your account so you can revisit them; you can delete a session anytime." },
    ],
    cta: { to: "/casebrief", label: "Analyse a Document" },
  },

  "judiciary-preparation": {
    icon: GraduationCap,
    eyebrow: "Judiciary Exam Preparation",
    h1: "AI Judiciary Exam Preparation — PrepMate AI",
    title: "AI Judiciary Exam Preparation & PYQ Practice | JusticeVerse PrepMate",
    description:
      "Crack the Judiciary, APO and law-officer exams with JusticeVerse PrepMate AI — personalised study plans, real PYQ practice, instant-graded mock MCQs, bare-act simplification, daily goals and streaks.",
    intro:
      "PrepMate AI is your personal AI mentor for the Indian Judiciary Services and law competitive exams. Get day-wise study plans, practice real previous-year questions (PYQs), attempt instant-graded mock MCQs, simplify bare-act sections, and stay consistent with daily goals and study streaks — all tailored to your target state judiciary.",
    features: [
      "Personalised day-wise and week-wise study plans with daily targets.",
      "Real PYQ practice and instant-graded mock MCQs with explanations.",
      "Bare-act simplification with illustrations and memory tricks.",
      "Daily goals, study streaks and performance analytics.",
      "State-specific prep — Gujarat, Delhi, Rajasthan, MP, UP and more.",
    ],
    faqs: [
      { q: "Which exams does PrepMate cover?", a: "Civil Judge / Judiciary Services, APO, Assistant Prosecutor, Law Officer and similar Indian law competitive exams." },
      { q: "Does it have previous-year questions?", a: "Yes — PrepMate includes a real PYQ database plus AI-generated exam-style practice questions with answers and explanations." },
      { q: "Is it personalised to my exam?", a: "Yes, set your target exam and PrepMate tailors study plans and questions to it." },
    ],
    cta: { to: "/prepmate", label: "Start Judiciary Prep" },
  },

  "best-legal-ai-for-advocates": {
    icon: Scale,
    eyebrow: "For Advocates",
    h1: "The Best Legal AI for Advocates in India",
    title: "Best Legal AI for Advocates & Lawyers in India | JusticeVerse",
    description:
      "JusticeVerse is the best AI for advocates in India — instant legal research, AI drafting, case-brief analysis, document translation and OCR for scanned files. Save hours on every matter.",
    intro:
      "JusticeVerse is built for practising advocates. Research any point of law, draft petitions and notices in minutes, brief lengthy judgments instantly, translate documents across languages, and read scanned case files with OCR — all in one premium workspace. Spend less time on routine work and more time winning cases.",
    features: [
      "Instant, India-specific legal research with verified citations.",
      "AI drafting for bail applications, petitions, notices and agreements.",
      "CaseBrief AI to summarise judgments and build arguments.",
      "Verse Translation for multilingual documents and clients.",
      "Device-bound secure accounts and unlimited plans for chambers & firms.",
    ],
    faqs: [
      { q: "Why is JusticeVerse good for advocates?", a: "It combines research, drafting, case briefing, document analysis and translation in one tool grounded in Indian law — cutting hours of routine work per matter." },
      { q: "Is there a plan for law firms?", a: "Yes — Advocate and Law Firm plans offer higher or unlimited usage, with team and unlimited CaseBrief options." },
      { q: "Can it handle scanned court files?", a: "Yes, CaseBrief and Verse Translation read scanned/image-only PDFs via OCR." },
    ],
    cta: { to: "/pricing", label: "See Advocate Plans" },
  },

  "best-legal-ai-for-law-students": {
    icon: BookOpen,
    eyebrow: "For Law Students",
    h1: "The Best Legal AI for Law Students",
    title: "Best Legal AI for Law Students & Judiciary Aspirants | JusticeVerse",
    description:
      "JusticeVerse is the best legal AI for law students — AI legal research, case-brief summaries, bare-act simplification and PrepMate judiciary exam preparation with PYQs. Affordable student plans from ₹29.",
    intro:
      "JusticeVerse helps law students learn faster and prepare smarter. Research any topic, get clear case briefs, simplify difficult bare-act sections, and prepare for the Judiciary and other exams with PrepMate AI — including real PYQs and instant-graded mocks. Affordable student pricing makes it easy to start.",
    features: [
      "AI legal research and structured case briefs for moots and assignments.",
      "Bare-act simplification with examples and memory aids.",
      "PrepMate AI for judiciary exam prep — study plans, PYQs and mocks.",
      "Free CaseBriefs for every student to start.",
      "Student plan from just ₹29/month.",
    ],
    faqs: [
      { q: "Is JusticeVerse affordable for students?", a: "Yes — the Student plan starts at ₹29/month, and every student gets free CaseBriefs and free research messages to begin." },
      { q: "Can it help with moot courts and assignments?", a: "Yes — use AI research and case briefs to find authorities, understand ratios and structure arguments." },
      { q: "Does it help with judiciary preparation?", a: "Yes — PrepMate AI offers personalised plans, real PYQs and instant-graded mock tests." },
    ],
    cta: { to: "/prepmate", label: "Start Learning Free" },
  },

  "justiceverse-ai": {
    icon: Crown,
    brand: true,
    eyebrow: "JusticeVerse · Official",
    h1: "JusticeVerse — India's Premier Legal AI Platform",
    title: "JusticeVerse | JusticeVerse AI — Official Legal AI Platform",
    description:
      "JusticeVerse (Justice Verse) is India's premier AI legal platform. The official JusticeVerse AI for legal research, drafting, case briefs, document translation and judiciary exam prep. Visit justice-verse.com.",
    intro:
      "JusticeVerse — also written as Justice Verse — is India's premier AI legal platform, available at justice-verse.com. JusticeVerse AI (JusticeVerse Legal AI) brings together six intelligent modules: JusticeVerse AI research, Counsel AI drafting, CaseBrief AI, Verse Translation, PrepMate AI judiciary prep, and Articles & Daily Digest. It is the official JusticeVerse website and product.",
    features: [
      "JusticeVerse AI — multilingual legal research grounded in Indian law.",
      "Counsel AI — court-ready legal drafting.",
      "CaseBrief AI — instant judgment briefs with OCR for scanned files.",
      "Verse Translation — legal document translation in 23 languages.",
      "PrepMate AI — judiciary and law exam preparation.",
      "Articles & Daily Digest & Careers — curated judgments, news and legal openings.",
    ],
    faqs: [
      { q: "What is JusticeVerse?", a: "JusticeVerse (Justice Verse) is India's premier AI legal platform offering AI legal research, drafting, case briefs, document translation and judiciary exam preparation. The official site is justice-verse.com." },
      { q: "Is JusticeVerse AI free to try?", a: "Yes — you can start free with research messages and free CaseBriefs, then upgrade to a paid plan from ₹29/month." },
      { q: "What is the official JusticeVerse website?", a: "The official website is https://justice-verse.com." },
      { q: "Who is JusticeVerse for?", a: "Advocates, law firms, law students, judiciary aspirants, judges and anyone needing trustworthy legal help in India." },
    ],
    cta: { to: "/", label: "Explore JusticeVerse" },
  },

  "best-indian-legal-ai": {
    icon: ShieldCheck,
    eyebrow: "Best Indian Legal AI",
    h1: "The Best Indian Legal AI Platform",
    title: "Best Indian Legal AI — Built for Indian Law | JusticeVerse",
    description:
      "JusticeVerse is the best Indian legal AI — purpose-built for Indian law (BNS, BNSS, IPC, CrPC, CPC), multilingual across 11+ languages, with research, drafting, case briefs, translation and judiciary prep.",
    intro:
      "Unlike generic chatbots, JusticeVerse is built specifically for Indian law. It understands the new criminal codes (BNS, BNSS, Bharatiya Sakshya Adhiniyam) alongside the IPC, CrPC, CPC and the Constitution, works across 11+ Indian languages, and reads scanned Indian court documents with OCR. That focus makes it the best Indian legal AI for real practice and study.",
    features: [
      "Purpose-built for Indian statutes and the new criminal codes.",
      "11+ Indian languages for research and answers.",
      "OCR tuned for scanned Indian court documents.",
      "Verified-citation policy to avoid fabricated case law.",
      "Affordable plans designed for the Indian market.",
    ],
    faqs: [
      { q: "Why is JusticeVerse the best Indian legal AI?", a: "It is purpose-built for Indian law and languages, handles scanned Indian court files, and enforces a verified-citation policy — going far beyond generic global AI tools." },
      { q: "Does it know the new criminal laws?", a: "Yes — BNS, BNSS and the Bharatiya Sakshya Adhiniyam, along with the IPC, CrPC, CPC and the Constitution." },
      { q: "Is it multilingual?", a: "Yes — it works across 11+ Indian languages." },
    ],
    cta: { to: "/justicebot", label: "Try the Best Indian Legal AI" },
  },

  "most-accurate-legal-ai": {
    icon: Award,
    eyebrow: "Most Accurate Legal AI",
    h1: "The Most Accurate Legal AI for Citations",
    title: "Most Accurate Legal AI — No Fake Citations | JusticeVerse",
    description:
      "JusticeVerse is engineered for accuracy — a strict anti-fabrication policy means it only cites real, verifiable judgments and abstains rather than inventing fake case law. The most accurate legal AI for Indian law.",
    intro:
      "Accuracy is everything in law. JusticeVerse is engineered with a strict anti-fabrication policy: it only cites judgments it is confident are real, never invents case names, citations or neutral citations, and clearly says 'no verified judgment found' rather than guessing. Combined with India-specific grounding, that makes it the most accurate legal AI for serious legal work.",
    features: [
      "Strict anti-fabrication policy — no invented cases or citations.",
      "Abstains honestly when a verified judgment doesn't exist.",
      "India-specific grounding in current statutes and codes.",
      "Structured, source-aware legal reasoning.",
      "Built for professionals who can't afford hallucinated authorities.",
    ],
    faqs: [
      { q: "How does JusticeVerse avoid fake citations?", a: "It is instructed to cite only judgments it is highly confident are real, never fabricate citation numbers, and explicitly state when no verified judgment exists — giving the correct statute instead." },
      { q: "Should I still verify citations?", a: "Yes. JusticeVerse dramatically reduces fabricated citations, but you should always confirm any authority against the official record before relying on it in court." },
      { q: "What makes it more accurate than generic AI?", a: "Its India-specific grounding and anti-fabrication rules are tuned for legal use, unlike general-purpose chatbots." },
    ],
    cta: { to: "/justicebot", label: "Experience Accurate Legal AI" },
  },
};

export const LANDING_SLUGS = Object.keys(LANDING_PAGES);
